import { Box, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import EditPencilIcon from "src/assets/icons/EditPencilIcon";

import WhiteCard from "src/shared/components/WhiteCard";
import EditContactModal from "./components/EditContactModal";
import { formatPhoneNumber } from "src/utils/formatPhoneNumber";
import { useIsMobile } from "src/hooks/useIsMobile";
import { usePatient } from "src/context/PatientProvider";
import { useTranslation } from "react-i18next";

interface Props {
  page: string;
}
const ContactInformation = ({ page }: Props) => {
  const isMobile = useIsMobile();
  const firstName = usePatient((patient) => patient.profile.first_name);
  const lastName = usePatient((patient) => patient.profile.last_name);
  const dob = usePatient((patient) => patient.profile.dob);
  const email = usePatient((patient) => patient.profile.email);
  const phoneNumber = usePatient(
    (patient) => patient.profile.phone_number ?? ""
  );
  const [openModal, setOpenModal] = useState<boolean>(false);
  const component = "contactCard";
  const { t } = useTranslation();

  const patientFullName = useMemo(() => {
    return `${firstName} ${lastName}`;
  }, [firstName, lastName]);

  const formattedDob = useMemo(() => {
    if (!dob) return "";

    return format(new Date(dob), "LLLL dd, yyyy");
  }, [dob]);

  const formattedPhoneNumber = useMemo(() => {
    return formatPhoneNumber(phoneNumber);
  }, [phoneNumber]);

  return (
    <>
      <WhiteCard>
        <Stack direction="row" gap={3}>
          <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
            {t(`${page}.${component}.title`)}
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            sx={{ gap: 0.5, cursor: "pointer" }}
            onClick={() => setOpenModal(true)}
          >
            <EditPencilIcon />
            <Typography sx={{ color: "#00B6B9" }}>
              {t(`${page}.edit`)}
            </Typography>
          </Box>
        </Stack>
        <Stack
          direction={isMobile ? "column" : "row"}
          justifyContent="space-between"
          gap={isMobile ? 2 : 0}
        >
          <Stack gap={0.5}>
            <Typography variant="subtitle1">
              {t(`${page}.${component}.full_name`)}
            </Typography>
            <Typography>{patientFullName}</Typography>
          </Stack>
          <Stack gap={0.5}>
            <Typography variant="subtitle1">
              {t(`${page}.${component}.email`)}
            </Typography>
            <Typography>{email}</Typography>
          </Stack>
          <Stack gap={0.5}>
            <Typography variant="subtitle1">
              {t(`${page}.${component}.phone`)}
            </Typography>
            <Typography>{formattedPhoneNumber}</Typography>
          </Stack>
          <Stack gap={0.5}>
            <Typography variant="subtitle1">
              {t(`${page}.${component}.birthday`)}
            </Typography>
            <Typography>{formattedDob}</Typography>
          </Stack>
        </Stack>
      </WhiteCard>
      <EditContactModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        formattedPhoneNumber={formattedPhoneNumber}
      />
    </>
  );
};

export default ContactInformation;
